import { Editor } from '@meitu/saas-editor-core';
import { Download, downloadExtension } from '@meitu/saas-editor-core/es/editor/extensions/download';
import { Preview, previewExtension } from '@meitu/saas-editor-core/es/editor/extensions/preview';
import { Template, templateExtension } from '@meitu/saas-editor-core/es/editor/extensions/template';
import { Wasm, wasmExtension } from '@meitu/saas-editor-core/es/editor/extensions/wasm';
import { TextLayerExtension, textLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { BackgroundLayer, backgroundLayerPlugin } from '@meitu/saas-editor-core/es/layers/background-layer';
import { BrushLayer, brushLayerPlugin } from '@meitu/saas-editor-core/es/layers/brush-layer';
import { ChartLayer, chartLayerPlugin } from '@meitu/saas-editor-core/es/layers/chart-layer';
import { GroupLayer, groupLayerPlugin } from '@meitu/saas-editor-core/es/layers/group-layer';
import { ImageLayer, imageLayerPlugin } from '@meitu/saas-editor-core/es/layers/image-layer';
import { MainLayer, mainLayerPlugin } from '@meitu/saas-editor-core/es/layers/main-layer';
import { MarkupLayer, markupLayerPlugin } from '@meitu/saas-editor-core/es/layers/markup-layer';
import { MediaLayer, mediaLayerPlugin } from '@meitu/saas-editor-core/es/layers/media-layer';
import { QRCodeLayer, qrCodeLayerPlugin } from '@meitu/saas-editor-core/es/layers/qr-code-layer';
import { TableLayer, tableLayerPlugin } from '@meitu/saas-editor-core/es/layers/table-layer';
import { VectorLayer, vectorLayerPlugin } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { WatermarkLayer, watermarkLayerPlugin } from '@meitu/saas-editor-core/es/layers/watermark-layer';
import { BrushDrawPlugin, brushDrawPlugin } from '@meitu/saas-editor-core/es/plugins/brush-draw';
import { chartHoverPlugin } from '@meitu/saas-editor-core/es/plugins/chart-hover';
import { ClipboardPlugin, clipboardPlugin } from '@meitu/saas-editor-core/es/plugins/clipboard';
import { ContextMenuPlugin, contextMenuPlugin } from '@meitu/saas-editor-core/es/plugins/context-menu';
import { CropBackground, cropBackgroundPlugin } from '@meitu/saas-editor-core/es/plugins/crop-background';
import { CropImgPlugin, cropImgPlugin } from '@meitu/saas-editor-core/es/plugins/crop-img';
import { CropStagePlugin, cropStagePlugin } from '@meitu/saas-editor-core/es/plugins/crop-stage';
import { DeformationImgPlugin, deformationImgPlugin } from '@meitu/saas-editor-core/es/plugins/deformation-img';
import { displacementPlugin } from '@meitu/saas-editor-core/es/plugins/displacement';
import { DragLayerPlugin, dragLayerPlugin } from '@meitu/saas-editor-core/es/plugins/drag-layer';
import { dropImgPlugin } from '@meitu/saas-editor-core/es/plugins/drop-img';
import { EliminatePenPlugin, eliminatePenPlugin } from '@meitu/saas-editor-core/es/plugins/eliminate-pen';
import {
    hotkeyEventMakeupPlugin,
    hotkeyEventPlugin,
    hotkeyEventRedoUndoPlugin,
    hotkeyEventScalePlugin,
    hotkeyEventSelectAllPlugin,
} from '@meitu/saas-editor-core/es/plugins/hotkey-event';
import { ImageCutoutPlugin, imageCutoutPlugin } from '@meitu/saas-editor-core/es/plugins/image-cutout';
import { ImageEnhancePlugin, imageEnhancePlugin } from '@meitu/saas-editor-core/es/plugins/image-enhance';
import { IntelligentColorPlugin, intelligentColorPlugin } from '@meitu/saas-editor-core/es/plugins/intelligent-color';
import { layerLoadingPlugin } from '@meitu/saas-editor-core/es/plugins/layer-loading';
import { layerShinePlugin } from '@meitu/saas-editor-core/es/plugins/layer-shine';
import { MarkupDrawPlugin, markupDrawPlugin } from '@meitu/saas-editor-core/es/plugins/markup-draw';
import { maskLoadingPlugin } from '@meitu/saas-editor-core/es/plugins/mask-loading';
import {
    mouseOperateActivePlugin,
    mouseOperateHoverPlugin,
    mouseOperateMakeupPlugin,
} from '@meitu/saas-editor-core/es/plugins/mouse-operate';
import { ReferenceLines, referenceLinesPlugin } from '@meitu/saas-editor-core/es/plugins/reference-lines';
import { refreshImageMaskEffectPlugin } from '@meitu/saas-editor-core/es/plugins/refresh-image-mask-effect';
import { RulerBar, rulerBarPlugin } from '@meitu/saas-editor-core/es/plugins/ruler-bar';
import { shapeTextRefresherPlugin } from '@meitu/saas-editor-core/es/plugins/shape-text-refresher';
import { smartGuidesPlugin } from '@meitu/saas-editor-core/es/plugins/smart-guides';
import { StageGridPlugin, stageGridPlugin } from '@meitu/saas-editor-core/es/plugins/stage-grid';
import { stageHoverPlugin } from '@meitu/saas-editor-core/es/plugins/stage-hover';
import { stageOperatePlugin } from '@meitu/saas-editor-core/es/plugins/stage-operate';
import { StageToolbar, stageToolbarPlugin } from '@meitu/saas-editor-core/es/plugins/stage-toolbar';
import { SvipWatermarkPlugin, svipWatermarkPlugin } from '@meitu/saas-editor-core/es/plugins/svip-watermark';
import { TableAction, tableEditPlugin } from '@meitu/saas-editor-core/es/plugins/table-edit';
import { TextEditPlugin, textEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';
import { PUBLIC_URL } from './react-process-env';

// 编辑器实例类型
export interface IEditor extends Editor {
    // 功能插件
    download: Download;
    preview: Preview;
    template: Template;
    wasm: Wasm;
    dragLayer: DragLayerPlugin;
    tableAction: TableAction;
    brushDraw: BrushDrawPlugin;
    clipboard: ClipboardPlugin;
    contextMenu: ContextMenuPlugin;
    cropBackground: CropBackground;
    cropImg: CropImgPlugin;
    cropStage: CropStagePlugin;
    deformationImg: DeformationImgPlugin;
    eliminatePen: EliminatePenPlugin;
    imageCutout: ImageCutoutPlugin;
    imageEnhance: ImageEnhancePlugin;
    intelligentColor: IntelligentColorPlugin;
    markupDraw: MarkupDrawPlugin;
    referenceLines: ReferenceLines;
    rulerBar: RulerBar;
    stageGrid: StageGridPlugin;
    textEdit: TextEditPlugin;
    svipWatermark: SvipWatermarkPlugin;
    stageToolbar: StageToolbar;
    // 挂载图层提示
    backgroundLayer: BackgroundLayer;
    brushLayer: BrushLayer;
    chartLayer: ChartLayer;
    groupLayer: GroupLayer;
    imageLayer: ImageLayer;
    mainLayer: MainLayer;
    markupLayer: MarkupLayer;
    mediaLayer: MediaLayer;
    qrCodeLayer: QRCodeLayer;
    tableLayer: TableLayer;
    textLayer: TextLayerExtension;
    vectorLayer: VectorLayer;
    watermarkLayer: WatermarkLayer;
}

export const editor = new Editor('editor-container', {
    PUBLIC_URL: PUBLIC_URL,
    padding: 35,
}) as IEditor;

// 注册插件
editor.registerPlugins([
    { plugin: backgroundLayerPlugin },
    { plugin: brushLayerPlugin },
    { plugin: chartLayerPlugin },
    { plugin: groupLayerPlugin },
    { plugin: imageLayerPlugin },
    { plugin: mainLayerPlugin },
    { plugin: markupLayerPlugin },
    { plugin: mediaLayerPlugin },
    { plugin: qrCodeLayerPlugin },
    { plugin: tableLayerPlugin },
    { plugin: textLayerExtension },
    { plugin: vectorLayerPlugin },
    { plugin: watermarkLayerPlugin },
    { plugin: downloadExtension },
    { plugin: previewExtension },
    { plugin: templateExtension },
    { plugin: wasmExtension },
    { plugin: brushDrawPlugin },
    { plugin: chartHoverPlugin },
    { plugin: clipboardPlugin },
    { plugin: contextMenuPlugin },
    { plugin: cropBackgroundPlugin },
    { plugin: cropImgPlugin },
    { plugin: dropImgPlugin },
    { plugin: cropStagePlugin },
    { plugin: deformationImgPlugin },
    { plugin: displacementPlugin },
    { plugin: dragLayerPlugin },
    { plugin: eliminatePenPlugin },
    { plugin: hotkeyEventMakeupPlugin },
    { plugin: hotkeyEventPlugin },
    { plugin: hotkeyEventRedoUndoPlugin },
    { plugin: hotkeyEventScalePlugin },
    { plugin: hotkeyEventSelectAllPlugin },
    { plugin: imageCutoutPlugin },
    { plugin: imageEnhancePlugin },
    { plugin: intelligentColorPlugin },
    { plugin: layerLoadingPlugin },
    { plugin: layerShinePlugin },
    { plugin: markupDrawPlugin },
    { plugin: maskLoadingPlugin },
    { plugin: mouseOperateActivePlugin },
    { plugin: mouseOperateHoverPlugin },
    { plugin: mouseOperateMakeupPlugin },
    { plugin: referenceLinesPlugin },
    { plugin: refreshImageMaskEffectPlugin },
    { plugin: rulerBarPlugin },
    { plugin: shapeTextRefresherPlugin },
    { plugin: smartGuidesPlugin },
    { plugin: stageGridPlugin },
    { plugin: stageHoverPlugin },
    { plugin: stageOperatePlugin },
    { plugin: stageToolbarPlugin },
    { plugin: svipWatermarkPlugin },
    { plugin: tableEditPlugin },
    { plugin: textEditPlugin },
]);
// 挂载实例
editor.registerInstance({
    // 功能插件
    download: editor.getInstance(Download),
    preview: editor.getInstance(Preview),
    template: editor.getInstance(Template),
    wasm: editor.getInstance(Wasm),
    dragLayer: editor.getInstance(DragLayerPlugin),
    tableAction: editor.getInstance(TableAction),
    brushDraw: editor.getInstance(BrushDrawPlugin),
    clipboard: editor.getInstance(ClipboardPlugin),
    contextMenu: editor.getInstance(ContextMenuPlugin),
    cropBackground: editor.getInstance(CropBackground),
    cropImg: editor.getInstance(CropImgPlugin),
    cropStage: editor.getInstance(CropStagePlugin),
    deformationImg: editor.getInstance(DeformationImgPlugin),
    eliminatePen: editor.getInstance(EliminatePenPlugin),
    imageCutout: editor.getInstance(ImageCutoutPlugin),
    imageEnhance: editor.getInstance(ImageEnhancePlugin),
    intelligentColor: editor.getInstance(IntelligentColorPlugin),
    markupDraw: editor.getInstance(MarkupDrawPlugin),
    referenceLines: editor.getInstance(ReferenceLines),
    rulerBar: editor.getInstance(RulerBar),
    stageGrid: editor.getInstance(StageGridPlugin),
    textEdit: editor.getInstance(TextEditPlugin),
    svipWatermark: editor.getInstance(SvipWatermarkPlugin),
    stageToolbar: editor.getInstance(StageToolbar),
    // 挂载图层
    backgroundLayer: editor.getInstance(BackgroundLayer),
    brushLayer: editor.getInstance(BrushLayer),
    chartLayer: editor.getInstance(ChartLayer),
    groupLayer: editor.getInstance(GroupLayer),
    imageLayer: editor.getInstance(ImageLayer),
    mainLayer: editor.getInstance(MainLayer),
    markupLayer: editor.getInstance(MarkupLayer),
    mediaLayer: editor.getInstance(MediaLayer),
    qrCodeLayer: editor.getInstance(QRCodeLayer),
    tableLayer: editor.getInstance(TableLayer),
    textLayer: editor.getInstance(TextLayerExtension),
    vectorLayer: editor.getInstance(VectorLayer),
    watermarkLayer: editor.getInstance(WatermarkLayer),
});

if (process.env.NODE_ENV !== 'release') {
    // @ts-expect-error 开发环境挂载
    window.editor = editor;
}
